// @ts-strict-ignore
import { Typography } from 'onboarding-components';
import * as React from 'react';
import clsx from 'clsx';
import { RiveState, useRive, useStateMachineInput } from '@rive-app/react-canvas';
import isEmpty from 'lodash/isEmpty';
import { SelectReact } from 'common/components/select';
import { SelectTheme } from 'common/components/select/constants';
import { SvgIconReact } from 'common/components/svg-icon';
import { IMMERSIVE_ASSETS } from 'common/onboarding/immersive-onboarding/assets';
import { DEFAULT_INDUSTRY_KEY, EVENT_INPUT_MAX } from 'common/onboarding/immersive-onboarding/constants';
import { TooltipReact } from 'common/components/tooltip';
import { Placement } from 'common/components/tooltip/types';
import { Banner, BannerType } from 'common/onboarding/immersive-onboarding/components/banner';
import { Content, Header } from 'common/onboarding/immersive-onboarding/components';
import { TextInput } from 'common/onboarding/immersive-onboarding/components/text-input';
import { IndustryKey, SetupGuideContext, Step, SubStep, UserEventPropertiesMapContext } from 'common/onboarding/immersive-onboarding/types';
import { promptIntroModal } from 'common/widgets/intro';
import { track, TrackingEvent, TrackingProperty } from 'common/onboarding/immersive-onboarding/tracking';
import rocketImg from 'common/onboarding/immersive-onboarding/assets/rocket.svg';
import { ApiClient } from 'common/onboarding/immersive-onboarding/api-client';
import { scrollToStep } from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import { useCurrentUser } from 'common/contexts/react/current-user-context';
import { useTranslation } from 'common/i18n/hooks';
import { Section } from '../section/section';
import { EventChartPreviewCarousel } from './chart-preview';
import { ReadyToConnectV2 } from './substeps';
import { EventSubsteps } from './phase-1.1';
import { getDefaultEventList, getDefaultEventPropertiesMap, getMapFromDictionary, getResolvedIndustrySpecs } from './util';
import { HelpText } from './phase-1.1/help-text';
import { EventSubStepContext } from './common';
import styles from './events.module.styl';
import { HelpTextType } from './types';
const EventBanner = () => {
  const currentUser = useCurrentUser();
  const {
    t
  } = useTranslation();
  return <Banner type={BannerType.Marketing} data-sentry-element="Banner" data-sentry-component="EventBanner" data-sentry-source-file="events.tsx">
      <Banner.Content title={t("onboarding.events.new-to-analytics")} data-sentry-element="unknown" data-sentry-source-file="events.tsx">
        <Banner.Image src={rocketImg} height={40} width={31} data-sentry-element="unknown" data-sentry-source-file="events.tsx" />
      </Banner.Content>
      <Banner.Action actionName={t("onboarding.events.learn-analytics")} action={() => {
      var _currentUser$get$valu;
      promptIntroModal({
        'first-name': (((_currentUser$get$valu = currentUser.get().value) === null || _currentUser$get$valu === void 0 ? void 0 : _currentUser$get$valu.name) || "").split(" ")[0]
      });
      track(TrackingEvent.EventsLearnMoreClick);
    }} data-sentry-element="unknown" data-sentry-source-file="events.tsx" />
    </Banner>;
};
const EventSelector = (_ref: {
  selected: IndustryKey;
  onIndustryKeyChange: TODO;
  events: string[];
  updateEvents: TODO;
}) => {
  let {
    selected,
    onIndustryKeyChange,
    events,
    updateEvents
  }: {
    selected: IndustryKey;
    onIndustryKeyChange: TODO;
    events: string[];
    updateEvents: TODO;
  } = _ref;
  const translation = useTranslation();
  const {
    t
  } = translation;
  const industrySpecs = getResolvedIndustrySpecs({
    i18n: translation
  });
  const industryKeys = Object.keys(industrySpecs) as IndustryKey[];
  const dropdownOptions = industryKeys.map(industryKey => ({
    value: industryKey,
    label: industrySpecs[industryKey].display_name
  }));
  const deleteEvent = eventIndex => {
    const updated = events.filter((_, index) => index !== eventIndex);
    updateEvents(updated);
  };
  const inputValidator = val => {
    return val.trim().length > 0;
  };
  return <div className={styles.sectionTile} data-sentry-component="EventSelector" data-sentry-source-file="events.tsx">
      <SelectReact className={styles.industrySelector} sections={[{
      items: dropdownOptions
    }]} disableSearch onChange={ev => {
      onIndustryKeyChange({
        industryKey: ev.detail.item.value as IndustryKey
      });
    }} theme={SelectTheme.Tertiary} alwaysShowCaret menuWidth={150} data-sentry-element="SelectReact" data-sentry-source-file="events.tsx">
        <div slot={"trigger"}>
          <Typography weight='regular' variant='bodySm' style={{
          fontSize: 14
        }} data-sentry-element="Typography" data-sentry-source-file="events.tsx">
            {industrySpecs[selected].display_name}
            {" "}
            <SvgIconReact icon='caret-down' style={{
            minHeight: 17,
            height: 17
          }} data-sentry-element="SvgIconReact" data-sentry-source-file="events.tsx" />
          </Typography>
        </div>
      </SelectReact>
      <div className={styles.eventContainer}>
        {events.map((event, eventIndex) => <div key={"".concat(industrySpecs[selected].display_name, "-").concat(event)} className={styles.eventEditorRow}>
            <TextInput validator={inputValidator} hideValidCheckMark defaultValue={event} onBlur={value => {
          const updated = events.map((ev, index) => index === eventIndex ? value : ev);
          updateEvents(updated);
        }} />
            <div className={clsx(styles.deleteIconWrapper, {
          [styles.disabled]: events.length === 1 && eventIndex === 0
        })} onClick={() => events.length > 1 && deleteEvent(eventIndex)}>
              <SvgIconReact className={styles.deleteIcon} icon={"trashcan"} />
              {events.length === 1 && eventIndex === 0 && <TooltipReact placement={Placement.Top} text={t("onboarding.events.choose-one-event")} />}
            </div>
          </div>)}
        {events.length < EVENT_INPUT_MAX && <TextInput key={Math.random()} // Hack to trick React rendering...
      validator={inputValidator} hideValidCheckMark placeholder={t("onboarding.events.add-own-event")} onBlur={value => {
        if (value.trim().length > 0) {
          const updated = [...events, value.trim()];
          updateEvents(updated);
        }
      }} style={{
        width: "calc(100% - 38px)"
      }} // 38px is the width of delete icon + gap from container
      />}
        {/* -1 due to existing input box (5 events + 1 event input) */}
        {events.length >= EVENT_INPUT_MAX - 1 && <Typography variant='captionSm' className={styles.eventLimitText}>
            {t("onboarding.events.tracking-suggestion")}
          </Typography>}
      </div>
    </div>;
};
const ReadyToConnectSection = _ref2 => {
  let {
    selected,
    disableNext
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    completed
  } = React.useContext(SetupGuideContext);
  const {
    setEventSubStep
  } = React.useContext(EventSubStepContext);
  return <Section scrollable active={completed[Step.Events]} subStepId={SubStep.ReadyToConnect} title={t("onboarding.events.one-step-away")} subTitle={t("onboarding.events.ready-to-connect")} spyCallback={() => {
    setEventSubStep(SubStep.ReadyToConnect);
  }} data-sentry-element="Section" data-sentry-component="ReadyToConnectSection" data-sentry-source-file="events.tsx">
      <ReadyToConnectV2 selected={selected} disableNext={disableNext && !completed[Step.Events]} data-sentry-element="ReadyToConnectV2" data-sentry-source-file="events.tsx" />
    </Section>;
};
const ConfirmEventsSection = _ref3 => {
  let {
    selected,
    updateSelected
  } = _ref3;
  const translation = useTranslation();
  const {
    t
  } = translation;
  const {
    setEventSubStep
  } = React.useContext(EventSubStepContext);
  const {
    setupGuideInfo,
    setSetupGuideInfo,
    setStep,
    completed
  } = React.useContext(SetupGuideContext);
  const {
    currentEventPropertiesMap,
    setCurrentEventPropertiesMap,
    eventKeys,
    setEventKeys
  } = React.useContext(UserEventPropertiesMapContext);

  // eslint-disable-next-line sonarjs/no-identical-functions
  const onIndustryKeyChange = (_ref4: {
    industryKey: IndustryKey;
  }) => {
    let {
      industryKey
    }: {
      industryKey: IndustryKey;
    } = _ref4;
    updateSelected(industryKey);
    let eventsPropertiesMap;
    if (selected !== industryKey && industryKey !== setupGuideInfo.industry) {
      eventsPropertiesMap = getDefaultEventPropertiesMap({
        industry: industryKey,
        i18n: translation
      });
    } else if (industryKey === setupGuideInfo.industry) {
      eventsPropertiesMap = getMapFromDictionary(setupGuideInfo.eventsPropertiesMap);
    }
    setCurrentEventPropertiesMap(eventsPropertiesMap);
    setEventKeys(Array.from(eventsPropertiesMap.keys())); // don't set key from effect -  set atomically
  };
  const updateEvents = (eventList: string[]) => {
    const updatedEventPropertiesMap = new Map<string, string[]>(eventList.map(event => {
      var _currentEventProperti;
      return [event, (_currentEventProperti = currentEventPropertiesMap.get(event)) !== null && _currentEventProperti !== void 0 ? _currentEventProperti : []];
    })) // update the map to carry over existing values
    ;
    setCurrentEventPropertiesMap(updatedEventPropertiesMap);
    setEventKeys(eventList); // update key separately to maintain order
  };
  return <Section active={completed[Step.Events]} className={styles.welcomePadding} scrollable spyCallback={() => {
    setEventSubStep(SubStep.EventConfirmation);
  }} subStepId={SubStep.EventConfirmation} sectionLabel='1' title={t("onboarding.events.choose-events")} subTitle={t("onboarding.events.chose-events-subtitle")} onNext={() => {
    ApiClient.updateSetupGuideInfo(setupGuideInfo.projectId, selected, null, currentEventPropertiesMap).then(result => {
      setSetupGuideInfo(result);
    });
    track(TrackingEvent.SubStepCompleted, {
      [TrackingProperty.SubStepName]: "Event confirmation"
    });
    scrollToStep(SubStep.Segmentation);
  }} skipCTA={t("onboarding.back")} onSkip={() => {
    track(TrackingEvent.Back, {
      [TrackingProperty.SubStepName]: "Event confirmation"
    });
    setStep(Step.Welcome);
  }} headerComponent={<EventBanner />} data-sentry-element="Section" data-sentry-component="ConfirmEventsSection" data-sentry-source-file="events.tsx">
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          <EventSelector selected={selected} events={eventKeys} onIndustryKeyChange={onIndustryKeyChange} updateEvents={updateEvents} data-sentry-element="EventSelector" data-sentry-source-file="events.tsx" />
        </div>
        {/* TODO(immersive onboarding) combine previewV1 styling after phase 2 100% rollout */}
        <div className={clsx(styles.preview, styles.previewV2)}>
          <EventChartPreviewCarousel events={eventKeys} data-sentry-element="EventChartPreviewCarousel" data-sentry-source-file="events.tsx" />
        </div>
      </div>
    </Section>;
};
const BreakdownPreviewSection = _ref5 => {
  let {
    selectedProperty,
    setSelectedProperty
  } = _ref5;
  const {
    t
  } = useTranslation();
  const [bounceInput, setBounceInput] = React.useState(false);
  const [showProperties, setShowProperties] = React.useState(false);
  const [helpTextType, setHelpTextType] = React.useState<HelpTextType>("event");

  // use context to hold the UI state here
  const {
    setEventSubStep
  } = React.useContext(EventSubStepContext);
  const {
    setupGuideInfo,
    setSetupGuideInfo,
    completed
  } = React.useContext(SetupGuideContext);
  const {
    currentEventPropertiesMap
  } = React.useContext(UserEventPropertiesMapContext);
  const stateMachineName = "Wave A";
  const stateMachineInputName = "Wave trigger";
  const stateMachineArtBoardName = "Wave A Artboard";
  const {
    rive,
    RiveComponent
  }: RiveState = useRive({
    src: IMMERSIVE_ASSETS.wave,
    stateMachines: [stateMachineName],
    artboard: stateMachineArtBoardName,
    autoplay: true
  });
  const waveInput = useStateMachineInput(rive, stateMachineName, stateMachineInputName);
  React.useEffect(() => {
    if (completed[Step.Events]) {
      setShowProperties(true);
    }
  }, [completed]);
  React.useEffect(() => {
    if (!showProperties) {
      setHelpTextType("event");
    } else if (!selectedProperty) {
      setHelpTextType("select-property");
    } else {
      setHelpTextType("edit-property");
    }
  }, [showProperties, selectedProperty]);
  return <Section active={completed[Step.Events]} scrollable sectionLabel='2' subStepId={SubStep.Segmentation} spyCallback={() => {
    setEventSubStep(SubStep.Segmentation);
  }} title={t("onboarding.events.choose-event-properties")} subTitle={<span>
          {t("onboarding.events.popular-event-properties")} {" "}
          <a href={"https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties"} target={"_blank"} onClick={() => {
      track(TrackingEvent.LearnMoreClick);
    }}>
            {t("common.learn-more")}
          </a>
        </span>} onNext={() => {
    track(TrackingEvent.SubStepCompleted, {
      [TrackingProperty.SubStepName]: "Property breakdown"
    });
    scrollToStep(SubStep.ReadyToConnect);
    ApiClient.updateSetupGuideInfo(setupGuideInfo.projectId, setupGuideInfo.industry, null, currentEventPropertiesMap).then(result => {
      setSetupGuideInfo(result);
    });
  }} disabledCallback={() => {
    setBounceInput(true);
    setTimeout(() => {
      setBounceInput(false);
      waveInput === null || waveInput === void 0 || waveInput.fire();
    }, 300);
  }} disableNext={!selectedProperty && !completed[Step.Events]} skipCTA={t("onboarding.back")} onSkip={showProperties && (() => {
    track(TrackingEvent.Back, {
      [TrackingProperty.SubStepName]: "Properties Editing"
    });
    scrollToStep(SubStep.EventConfirmation);
  })} data-sentry-element="Section" data-sentry-component="BreakdownPreviewSection" data-sentry-source-file="events.tsx">
      <EventSubsteps showProperties={showProperties} setShowProperties={setShowProperties} selectedProperty={selectedProperty} setSelectedProperty={setSelectedProperty} data-sentry-element="EventSubsteps" data-sentry-source-file="events.tsx" />
      <HelpText type={helpTextType} animationTrigger={{
      bounceInput
    }} data-sentry-element="HelpText" data-sentry-source-file="events.tsx">
        <RiveComponent style={{
        height: "28px",
        marginBottom: "4px",
        width: "28px"
      }} data-sentry-element="RiveComponent" data-sentry-source-file="events.tsx" />
      </HelpText>
    </Section>;
};
const EventsContent = () => {
  const {
    setupGuideInfo
  } = React.useContext(SetupGuideContext);
  const translation = useTranslation();
  const [selected, updateSelected] = React.useState<IndustryKey>(DEFAULT_INDUSTRY_KEY);
  const [userEventPropertiesMap, setUserEventPropertiesMap] = React.useState(new Map());
  const [eventKeys, setEventKeys] = React.useState<string[]>([]);
  const [selectedProperty, setSelectedProperty] = React.useState<string>(null);
  const mountRef = React.useRef(false);
  React.useEffect(() => {
    if (!mountRef.current) {
      if (isEmpty(setupGuideInfo.eventsPropertiesMap)) {
        setUserEventPropertiesMap(getDefaultEventPropertiesMap({
          industry: setupGuideInfo.industry,
          i18n: translation
        }));
        setEventKeys(getDefaultEventList({
          industry: setupGuideInfo.industry,
          i18n: translation
        }));
      } else {
        setUserEventPropertiesMap(new Map<string, string[]>(Object.keys(setupGuideInfo.eventsPropertiesMap).map(event => [event, setupGuideInfo.eventsPropertiesMap[event]])));
        setEventKeys(Object.keys(setupGuideInfo.eventsPropertiesMap));
      }
      updateSelected(setupGuideInfo.industry);
      mountRef.current = true;
    }
  }, [setupGuideInfo]);
  return <UserEventPropertiesMapContext.Provider value={{
    eventKeys,
    setEventKeys,
    currentEventPropertiesMap: userEventPropertiesMap,
    setCurrentEventPropertiesMap: setUserEventPropertiesMap
  }} data-sentry-element="unknown" data-sentry-component="EventsContent" data-sentry-source-file="events.tsx">
      <ConfirmEventsSection selected={selected} updateSelected={updateSelected} data-sentry-element="ConfirmEventsSection" data-sentry-source-file="events.tsx" />
      <BreakdownPreviewSection selectedProperty={selectedProperty} setSelectedProperty={setSelectedProperty} data-sentry-element="BreakdownPreviewSection" data-sentry-source-file="events.tsx" />
      <ReadyToConnectSection selected={selected} disableNext={!selectedProperty} data-sentry-element="ReadyToConnectSection" data-sentry-source-file="events.tsx" />
    </UserEventPropertiesMapContext.Provider>;
};
export const Events = () => {
  const {
    t
  } = useTranslation();
  const [eventSubStep, setEventSubStep] = React.useState<SubStep>(SubStep.EventConfirmation);
  const {
    completed
  } = React.useContext(SetupGuideContext);
  return <EventSubStepContext.Provider value={{
    eventSubStep,
    setEventSubStep
  }} data-sentry-element="unknown" data-sentry-component="Events" data-sentry-source-file="events.tsx">
      <Header title={t("onboarding.events.personalize-setup")} step={t("onboarding.events.step-two")} imgSrc={IMMERSIVE_ASSETS.toggles} duration={3} completed={completed[Step.Events]} data-sentry-element="Header" data-sentry-source-file="events.tsx" />
      <Content data-sentry-element="Content" data-sentry-source-file="events.tsx">
        <EventsContent data-sentry-element="EventsContent" data-sentry-source-file="events.tsx" />
      </Content>
    </EventSubStepContext.Provider>;
};